import { useEffect, useCallback, useRef } from "react";
import { getNextLiveStream } from "../../../shared/utils/playbackHelpers";
import { getCustomContextMetadata, trackConvivaCustomEvent } from "../../../shared/analytics/media";
import constants from "../../../shared/constants";
import { ANALYTICS_EVENT_TYPES } from "../../../shared/constants/analytics";
import { checkIsLookbackSupported } from "../../../shared/utils/epg";
import { trackGenericAction } from "../../../shared/analytics/dataLayer";
import { useReducers } from "../../../shared/hooks/useReducer";

const { REDUCER_TYPE } = constants;

export const useSOCUEffects = ({
  isLookbackStream,
  lookbackContent,
  history,
  restartAssetId,
  getMediaNodeMetaData,
  isConvivaAppTrackerEnabled,
  userProfile,
  appProvider,
  isInHome,
  updateLookbackStream,
  isRestartedStream,
  setRestartLiveStream,
  liveProgramCanUpdate,
  setIsLookbackStream,
  setUpdateLookbackStream,
  currentChannel,
  cancelTokenSource,
  channelID,
  useRoutePlaybackState,
  restartLiveStream,
}) => {
  const prevLiveProgramDetails = useRef(null); // ref to preserve details of the live stream across the re-renders, to be used for comparison with next stream during stream transition
  const nextLookbackProgram = useRef(false); // ref to persist and update next program for a lookback stream
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isDynatraceTrackingEnabled } = featureToggles;
  useEffect(() => {
    if (isLookbackStream) {
      if (!lookbackContent.current) {
        history.push("/"); // re-directing to home page in case user bookmarks a lookback player page and loads player directly
      } else {
        restartAssetId.current = lookbackContent.current.metadata?.contentId;
        trackGenericAction(ANALYTICS_EVENT_TYPES.LOOKBACK, getMediaNodeMetaData(), isDynatraceTrackingEnabled);
        if (isConvivaAppTrackerEnabled) {
          trackConvivaCustomEvent(
            ANALYTICS_EVENT_TYPES.LOOKBACK,
            getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
          );
        }
      }
    }
  }, [
    isLookbackStream,
    lookbackContent,
    history,
    restartAssetId,
    getMediaNodeMetaData,
    isConvivaAppTrackerEnabled,
    userProfile,
    appProvider,
    isInHome,
    isDynatraceTrackingEnabled,
  ]);

  useEffect(() => {
    if (updateLookbackStream && nextLookbackProgram.current) {
      lookbackContent.current = nextLookbackProgram.current;
      if (isRestartedStream.current) {
        setRestartLiveStream(false);
        liveProgramCanUpdate.current = true;
      }
      !isLookbackStream && setIsLookbackStream(true);
      setUpdateLookbackStream(false);
    }
  }, [
    updateLookbackStream,
    nextLookbackProgram,
    lookbackContent,
    isRestartedStream,
    setRestartLiveStream,
    liveProgramCanUpdate,
    isLookbackStream,
    setIsLookbackStream,
    setUpdateLookbackStream,
  ]);
  /**
   * Switches the player to lookback mode
   * @param {Object} nextLookbackProgramDetails
   */
  const triggerLookback = useCallback(
    (nextLookbackProgramDetails) => {
      if (checkIsLookbackSupported(nextLookbackProgramDetails?.metadata, currentChannel, isInHome)) {
        nextLookbackProgram.current = nextLookbackProgramDetails;
        setUpdateLookbackStream(true);
      }
    },
    [currentChannel, isInHome, nextLookbackProgram, setUpdateLookbackStream]
  );

  /**
   * Fetches next live stream in queue when a restarted stream is finished
   * @return {Object} next stream in queue
   */
  const fetchNextLiveStream = useCallback(async () => {
    const nextLiveProgram = await getNextLiveStream(
      appProvider,
      cancelTokenSource,
      channelID,
      prevLiveProgramDetails.current.prevLiveProgram.metadata.airingEndTime
    );
    return nextLiveProgram;
  }, [appProvider, channelID, cancelTokenSource, prevLiveProgramDetails]);
  /**
   * Starts over the stream for the given live program
   * @param {Object} liveProgramDetails
   */
  const triggerRestart = useCallback(
    (liveProgramDetails) => {
      useRoutePlaybackState.current = false;
      restartAssetId.current = liveProgramDetails?.metadata?.contentId;
      isLookbackStream && setIsLookbackStream(false);
      !restartLiveStream && setRestartLiveStream(true);
      trackGenericAction(ANALYTICS_EVENT_TYPES.RESTART, getMediaNodeMetaData(), isDynatraceTrackingEnabled);
      if (isConvivaAppTrackerEnabled) {
        trackConvivaCustomEvent(
          ANALYTICS_EVENT_TYPES.RESTART,
          getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
        );
      }
    },
    [
      useRoutePlaybackState,
      restartAssetId,
      isLookbackStream,
      setIsLookbackStream,
      restartLiveStream,
      setRestartLiveStream,
      getMediaNodeMetaData,
      isConvivaAppTrackerEnabled,
      userProfile,
      appProvider,
      isInHome,
      isDynatraceTrackingEnabled,
    ]
  );

  return { triggerLookback, fetchNextLiveStream, triggerRestart, prevLiveProgramDetails };
};
